import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ChevronRight, Star, Truck, CreditCard, Home as HomeIcon, Warehouse } from 'lucide-react';
import { cities, services } from '../data/locationsSeo';
import BookingWalkthrough from './BookingWalkthrough';
import Gallery from './Gallery';
import Testimonials from './Testimonials';
import FAQ from './FAQ';
import Footer from './Footer';

const SCHEMA_SCRIPT_ID = 'location-service-jsonld';

const LocationServicePage = () => {
  const { city: citySlug, service: serviceSlug } = useParams();

  const city = cities.find((c) => c.slug === citySlug);
  const service = services.find((s) => s.slug === serviceSlug);

  // Inject page-specific JSON-LD Schema (AutoDetailing) — mount/unmount cleanly
  useEffect(() => {
    if (!city || !service) return;

    const baseUrl = 'https://prowashmd.com';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'AutoDetailing',
      name: `${service.name} in ${city.name}, ${city.province}`,
      description: service.description,
      url: `${baseUrl}/locations/${city.slug}/${service.slug}`,
      image: `${baseUrl}/favicon.jpg`,
      telephone: '+1-604-613-8901',
      priceRange: '$$',
      areaServed: {
        '@type': 'City',
        name: city.name,
        containedInPlace: {
          '@type': 'AdministrativeArea',
          name: `${city.province}, Canada`,
        },
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: city.name,
        addressRegion: city.province,
        addressCountry: 'CA',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: city.geo.latitude,
        longitude: city.geo.longitude,
      },
      provider: {
        '@type': 'LocalBusiness',
        name: 'Pro-Wash Mobile Detailing',
        telephone: '+1-604-613-8901',
        url: baseUrl,
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5.0',
        reviewCount: '47',
        bestRating: '5',
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: `${service.name} — ${city.name}`,
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: service.name,
              description: service.description,
              areaServed: `${city.name}, ${city.province}`,
            },
          },
        ],
      },
    };

    // Remove any prior instance (SPA nav guard)
    const existing = document.getElementById(SCHEMA_SCRIPT_ID);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = SCHEMA_SCRIPT_ID;
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    // Meta title + description
    const prevTitle = document.title;
    document.title = `${service.name} in ${city.name}, ${city.province} | Pro-Wash Mobile Detailing`;

    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc ? metaDesc.getAttribute('content') : null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      'content',
      `${service.name} in ${city.name}, ${city.province}. ${service.tagline} Mobile detailing delivered to your home or workplace.`
    );

    // Meta Pixel tracking
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'ViewContent', {
        content_type: 'product',
        content_name: `${service.name} - ${city.name}`,
        content_category: 'Local Service Landing',
      });
    }

    return () => {
      const toRemove = document.getElementById(SCHEMA_SCRIPT_ID);
      if (toRemove) toRemove.remove();
      document.title = prevTitle;
      if (metaDesc && prevDesc !== null) {
        metaDesc.setAttribute('content', prevDesc);
      }
    };
  }, [city, service]);

  // If city or service slug is invalid, send to home (safer than blank page)
  if (!city || !service) {
    return <Navigate to="/" replace />;
  }

  const WorkspaceIcon = service.workspace === 'garage' ? Warehouse : HomeIcon;

  // Ceramic coating pages get their own hero video + playback rate
  const isCeramicCoating = service.slug === '8-year-signature-ceramic-coating';
  const isHeadlightRestoration = service.slug === 'permanent-headlight-restoration';
  const isExteriorOnly = service.slug === 'exterior-only';
  let heroVideoSrc = 'https://customer-assets-v7afamib.emergentagent.net/job_eddf8515-2938-471a-a6e5-081675307203/artifacts/xfmo8u4e_6157913-hd_1920_1080_30fps.mp4';
  let heroPoster = '/posters/default.jpg';
  let heroPlaybackRate = 0.6;
  if (isCeramicCoating) {
    heroVideoSrc = 'https://customer-assets-v7afamib.emergentagent.net/job_eddf8515-2938-471a-a6e5-081675307203/artifacts/ld39w3qh_6159183-hd_1920_1080_30fps.mp4';
    heroPoster = '/posters/coating.jpg';
    heroPlaybackRate = 0.75;
  } else if (isHeadlightRestoration) {
    heroVideoSrc = 'https://customer-assets-v7afamib.emergentagent.net/job_eddf8515-2938-471a-a6e5-081675307203/artifacts/qnkj2q7x_6312478-uhd_3840_2160_25fps.mp4';
    heroPoster = '/posters/headlight.jpg';
    heroPlaybackRate = 0.75;
  } else if (isExteriorOnly) {
    heroVideoSrc = 'https://customer-assets-v7afamib.emergentagent.net/job_eddf8515-2938-471a-a6e5-081675307203/artifacts/gd2djb7c_6159287-hd_1920_1080_30fps.mp4';
    heroPoster = '/posters/exterior.jpg';
    heroPlaybackRate = 0.75;
  }

  return (
    <div className="bg-[#0F172A] min-h-screen">
      {/* ============ PART 1: LOCAL SEO SECTION ============ */}
      <section
        data-testid="location-seo-header"
        className="relative overflow-hidden border-b border-slate-800"
      >
        {/* Background Video (matches homepage hero) */}
        <div className="absolute inset-0 w-full h-full bg-black">
          <video
            data-testid="location-hero-video"
            key={heroVideoSrc}
            className="w-full h-full object-cover"
            src={heroVideoSrc}
            poster={heroPoster}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onLoadedMetadata={(e) => { e.currentTarget.playbackRate = heroPlaybackRate; }}
          />
          {/* Dark overlays for legibility */}
          <div className="absolute inset-0 bg-black/60"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
        </div>

        <div className="container relative z-10 px-4 md:px-8 pt-24 pb-16 md:pt-32 md:pb-24">
          {/* Breadcrumbs */}
          <nav
            data-testid="location-breadcrumbs"
            className="flex items-center flex-wrap gap-x-2 gap-y-1 text-xs md:text-sm text-slate-400 mb-8"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight size={14} className="text-slate-600" />
            <span className="text-slate-500">Locations</span>
            <ChevronRight size={14} className="text-slate-600" />
            <span className="text-slate-500">{city.name}</span>
            <ChevronRight size={14} className="text-slate-600" />
            <span className="text-blue-400">{service.name}</span>
          </nav>

          {/* H1 */}
          <h1
            data-testid="location-h1"
            className="brand-display text-white leading-[1.05] tracking-tight text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-5"
          >
            {service.heroTitle} in {city.name}
          </h1>

          {/* Subhead */}
          <p
            data-testid="location-subhead"
            className="text-slate-300 max-w-3xl text-base md:text-xl font-light leading-relaxed mb-8"
          >
            {service.tagline} Delivered directly to your home or workplace in {city.name}.
          </p>

          {/* Trust Badges */}
          <div
            data-testid="location-trust-badges"
            className="flex flex-wrap gap-3 md:gap-4 mb-10"
          >
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full">
              <Truck size={16} className="text-blue-400" />
              <span className="text-sm font-semibold text-blue-100">100% Mobile Service</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full">
              <Star size={16} className="text-yellow-400 fill-yellow-400" />
              <span className="text-sm font-semibold text-yellow-100">5.0 ★ Google Rated</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full">
              <CreditCard size={16} className="text-green-400" />
              <span className="text-sm font-semibold text-green-100">Pay Upon Completion</span>
            </div>
          </div>

          {/* Service Inclusions Card */}
          <div
            data-testid="location-service-card"
            className="bg-slate-900/70 backdrop-blur-md border border-slate-800 rounded-2xl p-6 md:p-8 max-w-3xl"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
                <Star size={18} className="text-blue-400 fill-blue-400" />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white mb-1">
                  What&apos;s included with {service.name}
                </h2>
                <p className="text-xs uppercase tracking-widest text-blue-400 font-semibold">
                  {city.name}, {city.province}
                </p>
              </div>
            </div>

            <p className="text-slate-300 text-sm md:text-base leading-relaxed mb-5">
              {service.description}
            </p>

            {/* Workspace requirement */}
            <div className="flex items-start gap-3 p-4 rounded-xl bg-slate-950/60 border border-slate-800">
              <WorkspaceIcon size={18} className="text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-semibold text-white mb-1">
                  {service.workspace === 'garage'
                    ? 'Garage / covered workspace required'
                    : 'Driveway service available'}
                </div>
                <div className="text-xs text-slate-400 leading-relaxed">
                  {service.workspaceNote}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PART 2: FULL /BOOK-NOW PAGE CONTENT ============ */}
      {/* Rendered inline so customers can complete their booking on this page.
          Mirrors the structure of /app/frontend/src/components/BookNowPage.jsx */}
      <div data-testid="location-booking-embed">
        {/* Hidden SEO paragraph (same pattern as BookNowPage) */}
        <div className="sr-only">
          <h2>Book Your {service.name} in {city.name}</h2>
          <p>
            Pro-Wash Mobile Detailing brings professional {service.name.toLowerCase()} directly
            to homes and workplaces across {city.name}, {city.province}. Book online in minutes.
          </p>
        </div>

        <BookingWalkthrough />
        <Gallery />
        <Testimonials />
        <FAQ />
        <Footer />
      </div>
    </div>
  );
};

export default LocationServicePage;
